import { useQuery } from '@tanstack/react-query'
import { Megaphone, TriangleAlert } from 'lucide-react'
import { fetchContent, has, text } from '../api/content'

/**
 * A notice across the top of every public page.
 *
 * For the two things a studio needs to say at short notice: *"closed tonight,
 * ice"* and *"early bird signup is open"*. One field switches it on and off —
 * clearing the message hides the banner — rather than a message plus a separate
 * toggle that can disagree, where the failure is a studio that believes it
 * announced a closure and a banner nobody switched on.
 *
 * **This puts a notice on the website.** Sending one to 163 families is the
 * notification pipeline, which is M3 and priced separately (D-87, D-110).
 */
export default function AnnouncementBanner() {
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  if (!has(content, 'announcement.body')) return null

  const urgent = text(content, 'announcement.tone', 'notice').toLowerCase().startsWith('urgent')
  const link = text(content, 'announcement.link')

  return (
    <div
      role="status"
      className={`border-b ${urgent ? 'border-crit bg-crit-bg' : 'border-accent bg-accent-soft'}`}
    >
      <div className="mx-auto flex max-w-[56rem] flex-wrap items-center gap-x-3 gap-y-1 px-5 py-2.5">
        {urgent ? (
          <TriangleAlert size={15} strokeWidth={2.5} className="shrink-0 text-crit" />
        ) : (
          <Megaphone size={15} strokeWidth={2} className="shrink-0 text-accent-2" />
        )}

        <p className={`min-w-0 flex-1 text-[13.5px] font-semibold ${urgent ? 'text-crit' : 'text-ink'}`}>
          {text(content, 'announcement.body')}
        </p>

        {link && (
          <a
            href={link}
            className={`shrink-0 text-[13px] font-semibold underline ${urgent ? 'text-crit' : 'text-accent-2'}`}
          >
            {text(content, 'announcement.linkText', 'Find out more')}
          </a>
        )}
      </div>
    </div>
  )
}
